/**
 * P-007 — offline-first subsystem wiring (the umbrella of claims 1/11/16, minus the on-device-AI
 * element which is intentionally dropped per the server-side Bedrock thesis).
 *
 * Ties the modules together: connectivity detection → intelligent sync engine (priority + conflict
 * resolution) → encrypted offline store; plus a bridge to the service worker for predictive prefetch
 * and SW-timer offline alerts. Import initOfflineSubsystem() once at app start.
 */
import { ConnectivityMonitor, MODE } from './connectivity';
import { SyncEngine } from './syncEngine';
import { enqueue, putRecord, getRecord, queueSize } from './offlineStore';
import { sortByPriority, priorityFor, selectForBandwidth } from './priority';
import { resolveConflict } from './conflictResolution';

/** Default uploader: replays a queued edit against its endpoint; maps HTTP 409 → conflict. */
async function defaultUpload(edit) {
  try {
    const res = await fetch(edit.endpoint, {
      method: edit.method || 'POST',
      headers: { 'Content-Type': 'application/json', ...(edit.headers || {}) },
      body: JSON.stringify(edit.value),
    });
    if (res.ok) return { ok: true };
    if (res.status === 409) { const remote = await res.json().catch(() => ({})); return { conflict: true, remote }; }
    return { ok: false };
  } catch (_) {
    return { ok: false }; // network failure → stays queued
  }
}

let _handle = null;

export function initOfflineSubsystem({ uploadFn = defaultUpload, probeUrl = '/api/health' } = {}) {
  if (_handle) return _handle;

  const monitor = new ConnectivityMonitor({ probeUrl });
  const sync = new SyncEngine({ uploadFn });
  const unbindSync = sync.bindTo(monitor);
  monitor.start();

  // c14/c19 — register periodic background sync for SW-timer offline alerts (best-effort).
  if ('serviceWorker' in navigator && navigator.serviceWorker.ready) {
    navigator.serviceWorker.ready.then(async (reg) => {
      try {
        if (reg.periodicSync) {
          const status = await navigator.permissions?.query?.({ name: 'periodic-background-sync' }).catch(() => null);
          if (!status || status.state === 'granted') {
            await reg.periodicSync.register('healist-local-alerts', { minInterval: 60 * 60 * 1000 });
          }
        }
      } catch (_) { /* unsupported → the app can still trigger CHECK_LOCAL_ALERTS via message */ }
    });
  }

  const sw = () => navigator.serviceWorker?.controller;

  _handle = {
    monitor,
    sync,
    MODE,
    getMode: () => monitor.mode,
    subscribe: (fn) => monitor.subscribe(fn),
    /** Queue an offline edit (auto-syncs when connectivity returns). */
    queueEdit: (edit) => enqueue(edit),
    /** Cache a health record locally (encrypted) for offline reads / local alerts. */
    cacheRecord: (type, id, value) => putRecord(type, id, value),
    readRecord: (type, id) => getRecord(type, id),
    pendingCount: () => queueSize(),
    /** c7 — ask the SW to predictively prefetch URLs (upcoming appts / recurring meds). */
    // D11: the SW caches per user, so it needs the caller's identity; without a token nothing is prefetched.
    prefetch: (urls) => {
      const t = localStorage.getItem('token');
      sw()?.postMessage({ type: 'PREFETCH_HEALTH', urls, authorization: t ? `Bearer ${t}` : null });
    },
    /** c14/c19 — hand the SW a locally-computed reminder schedule to fire without network. */
    scheduleLocalAlerts: (alerts) => sw()?.postMessage({ type: 'SCHEDULE_LOCAL_ALERTS', alerts }),
    checkLocalAlerts: () => sw()?.postMessage({ type: 'CHECK_LOCAL_ALERTS' }),
    dispose: () => { unbindSync(); monitor.stop(); _handle = null; },
    // Pure/deterministic helpers exposed for verification (E2E) and offline-aware call sites.
    internals: { sortByPriority, priorityFor, selectForBandwidth, resolveConflict },
  };
  return _handle;
}

export function getOfflineSubsystem() { return _handle; }
export { MODE };
